import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * Opens a Stripe checkout URL using the appropriate method for the platform.
 * - Native (iOS/Android): Opens in-app browser (SFSafariViewController / Chrome Custom Tabs)
 *   and returns a promise that resolves when the browser is closed.
 * - Web: Opens in a new tab via window.open (existing behavior).
 *
 * @returns Promise that resolves to { closedByUser: true } on native when browser is closed,
 *          or { closedByUser: false } on web (fire-and-forget).
 */
export async function openPaymentUrl(url: string): Promise<{ closedByUser: boolean }> {
  if (!Capacitor.isNativePlatform()) {
    // Web: existing behavior
    const stripeWindow = window.open(url, '_blank');
    if (!stripeWindow) {
      window.location.href = url;
    }
    return { closedByUser: false };
  }

  // Native: open in-app browser and wait for it to close
  return new Promise<{ closedByUser: boolean }>((resolve) => {
    const listener = Browser.addListener('browserFinished', () => {
      listener.then(h => h.remove());
      resolve({ closedByUser: true });
    });

    Browser.open({ url, presentationStyle: 'fullscreen' });
  });
}

/**
 * Saves the Stripe session ID to localStorage so it can be retrieved
 * after the in-app browser closes on native.
 */
export function saveNativePaymentSession(sessionId: string, eventId: string) {
  localStorage.setItem('nativePaymentSession', JSON.stringify({ sessionId, eventId }));
}

/**
 * Retrieves and clears the saved native payment session.
 */
export function getNativePaymentSession(): { sessionId: string; eventId: string } | null {
  const raw = localStorage.getItem('nativePaymentSession');
  if (!raw) return null;
  localStorage.removeItem('nativePaymentSession');
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
